import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronLeft, Globe } from 'lucide-react';
import Logo from './Logo';
import { isNativePlatform } from '../lib/platform';
import AppleIAPButton from './AppleIAPButton';

/**
 * Reusable Upgrade Screen component for paywall pages
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon component to display in the header
 * @param {string} props.iconBgClass - Tailwind classes for icon background gradient (e.g., "from-blue-500 to-indigo-600")
 * @param {string} props.iconColorClass - Tailwind classes for icon color in features (e.g., "text-blue-500")
 * @param {string} props.buttonGradientClass - Tailwind classes for button gradient (e.g., "from-blue-500 to-indigo-600")
 * @param {string} props.title - Title of the feature
 * @param {string} props.description - Description text (defaults to "Fonctionnalité exclusive du Club 10M Investor")
 * @param {Array} props.features - Array of feature objects with { icon, title, description }
 * @param {string} props.currentTier - Current user subscription tier
 * @param {boolean} props.showHeader - Whether to show the header with logo (defaults to true)
 */
const UpgradeScreen = ({
  icon: IconComponent,
  iconBgClass = "from-blue-500 to-indigo-600",
  iconColorClass = "text-blue-500",
  buttonGradientClass = "from-blue-500 to-indigo-600",
  title,
  description = "Fonctionnalité exclusive du Club 10M Investor",
  features = [],
  currentTier = "free",
  showHeader = true
}) => {
  const navigate = useNavigate();

  const getTierLabel = (tier) => {
    if (tier === 'free') return 'Gratuit';
    if (tier === 'club' || tier === 'expert' || tier === 'founder') return 'Club 10M';
    return tier;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {showHeader && (
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Logo size="default" linkTo="/dashboard" />
            </div>
          </div>
        </header>
      )}

      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <Card className="max-w-lg mx-4 shadow-xl border-0">
          <CardHeader className="text-center pb-4">
            <div className={`mx-auto w-20 h-20 bg-gradient-to-br ${iconBgClass} rounded-2xl flex items-center justify-center mb-4`}>
              {IconComponent && <IconComponent className="h-10 w-10 text-white" />}
            </div>
            <CardTitle className="text-2xl text-[#0D47A1]">{title}</CardTitle>
            <CardDescription className="text-base">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              {features.map((feature, index) => {
                const FeatureIcon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    {FeatureIcon && <FeatureIcon className={`h-5 w-5 ${iconColorClass} mt-0.5`} />}
                    <div>
                      <p className="font-medium text-slate-900">{feature.title}</p>
                      <p className="text-sm text-slate-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {!isNativePlatform() && (
            <p className="text-center text-sm text-slate-500">
              Votre abonnement actuel : <Badge variant="outline" className="ml-1">{getTierLabel(currentTier)}</Badge>
            </p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            {isNativePlatform() ? (
              <div className="w-full">
                <AppleIAPButton onPurchaseSuccess={() => window.location.reload()} />
              </div>
            ) : (
              <Button 
                className={`w-full bg-gradient-to-r ${buttonGradientClass} hover:opacity-90 text-base py-6`}
                onClick={() => navigate('/pricing')}
                data-testid="upgrade-cta-button"
              >
                Rejoindre le Club 10M Investor
              </Button>
            )}
            <Button 
              variant="ghost" 
              className="w-full" 
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-button"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Retour au dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default UpgradeScreen;
